<script lang="ts" setup>
import { nextTick, ref, watch } from "vue";
import { onClickOutside, useMediaQuery } from "@vueuse/core";
import PresentationAnalyticsWorkspaceTree from "./PresentationAnalyticsWorkspaceTree.vue";

const props = defineProps<{
  modelValue?: string;
}>();

const emit = defineEmits<{
  change: [id: string | undefined];
}>();

const open = ref(false);
const selectedName = ref("");
const filter = ref<HTMLElement>();
const panel = ref<HTMLElement>();
const firstAction = ref<HTMLButtonElement>();
const trigger = ref<HTMLButtonElement>();
const isMobile = useMediaQuery("(max-width: 680px)");

watch(
  () => props.modelValue,
  (workspaceId) => {
    if (!workspaceId) {
      selectedName.value = "";
    }
  },
  { immediate: true },
);

function toggle() {
  if (open.value) {
    close();
    return;
  }

  open.value = true;
  void nextTick(() => firstAction.value?.focus());
}

function closeFilter(restoreTriggerFocus: boolean) {
  if (!open.value) {
    return;
  }

  open.value = false;
  if (restoreTriggerFocus) {
    void nextTick(() => trigger.value?.focus());
  }
}

function close() {
  closeFilter(true);
}

function trapMobileFocus(event: KeyboardEvent) {
  if (!isMobile.value || event.key !== "Tab" || !panel.value) {
    return;
  }

  const focusable = Array.from(
    panel.value.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((element) => element.getClientRects().length > 0);

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (!first || !last) {
    return;
  }

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

function selectAll() {
  selectedName.value = "";
  emit("change", undefined);
  close();
}

function selectWorkspace(id: string, name: string) {
  selectedName.value = name;
  emit("change", id);
  close();
}

onClickOutside(filter, () => closeFilter(isMobile.value));
</script>

<template>
  <div ref="filter" class="workspace_filter" @keydown.esc.stop.prevent="close">
    <button
      ref="trigger"
      type="button"
      class="filter_trigger"
      aria-haspopup="dialog"
      :aria-expanded="open"
      data-test="presentation-analytics-workspace-filter"
      @click="toggle"
    >
      <span class="material-symbols-outlined" aria-hidden="true">folder</span>
      <span class="filter_label">
        {{
          selectedName ||
          $t("presentationAnalyticsSelector.workspaceFilter.allWorkspaces")
        }}
      </span>
      <span class="material-symbols-outlined" aria-hidden="true">
        arrow_drop_down
      </span>
    </button>

    <div
      v-if="open"
      class="filter_backdrop"
      aria-hidden="true"
      @click="close"
    ></div>

    <section
      ref="panel"
      v-show="open"
      class="filter_panel"
      role="dialog"
      :aria-modal="isMobile ? 'true' : undefined"
      :aria-label="
        $t('presentationAnalyticsSelector.workspaceFilter.dialogLabel')
      "
      @keydown="trapMobileFocus"
    >
      <header>
        <strong>
          {{ $t("presentationAnalyticsSelector.workspaceFilter.title") }}
        </strong>
        <button
          type="button"
          class="close_button"
          :aria-label="
            $t('presentationAnalyticsSelector.workspaceFilter.close')
          "
          @click="close"
        >
          <span class="material-symbols-outlined">close</span>
        </button>
      </header>

      <div class="filter_content">
        <button
          ref="firstAction"
          type="button"
          class="all_workspaces"
          :class="{ selected: !modelValue }"
          :aria-current="!modelValue ? 'true' : undefined"
          @click="selectAll"
        >
          <span class="material-symbols-outlined" aria-hidden="true">
            account_tree
          </span>
          {{
            $t("presentationAnalyticsSelector.workspaceFilter.allWorkspaces")
          }}
        </button>

        <PresentationAnalyticsWorkspaceTree
          :selected="modelValue"
          @change="selectWorkspace"
          @selected-label="selectedName = $event"
        />
      </div>
    </section>
  </div>
</template>

<style scoped>
.workspace_filter {
  position: relative;
}

.filter_trigger {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  height: 2.4rem;
  max-width: 20rem;
  padding: 0 0.8rem;
  border: 1px solid var(--classic-border-strong, #bbb);
  border-radius: 0.3rem;
  background: var(--classic-surface, #fff);
  color: inherit;
  cursor: pointer;
}

.filter_trigger:hover {
  border-color: var(--classic-text-strong, #1a1918);
}

.filter_label {
  min-width: 8rem;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filter_panel {
  position: absolute;
  top: calc(100% + 0.4rem);
  right: 0;
  z-index: 50;
  width: min(24rem, 80vw);
  max-height: min(32rem, 70vh);
  overflow: hidden;
  border: 1px solid var(--classic-border, #ddd);
  border-radius: 0.45rem;
  background: var(--classic-surface, #fff);
  box-shadow: 0 0.7rem 2rem rgba(0, 0, 0, 0.16);
}

.filter_panel header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 3rem;
  padding: 0 0.8rem 0 1rem;
  border-bottom: 1px solid var(--classic-border, #ddd);
}

.close_button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
}

.filter_content {
  max-height: calc(min(32rem, 70vh) - 3rem);
  padding: 0.6rem;
  overflow: auto;
}

.all_workspaces {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
  min-height: 2.35rem;
  padding: 0.35rem 0.7rem;
  border: 0;
  border-radius: 0.3rem;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.all_workspaces:hover,
.all_workspaces.selected {
  background: var(--classic-surface-hover, #f5f5f5);
}

.all_workspaces.selected {
  box-shadow: inset 3px 0 0 var(--classic-accent, #34b5ba);
}

.filter_backdrop {
  display: none;
}

@media (max-width: 680px) {
  .workspace_filter {
    position: static;
  }

  .filter_trigger {
    width: 100%;
    max-width: none;
  }

  .filter_label {
    flex: 1;
  }

  .filter_backdrop {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 49;
    background: var(--classic-overlay, rgba(0, 0, 0, 0.45));
  }

  .filter_panel {
    position: fixed;
    inset: auto 0 0;
    width: auto;
    max-height: 78vh;
    border-radius: 0.8rem 0.8rem 0 0;
  }

  .filter_content {
    max-height: calc(78vh - 3rem);
  }
}
</style>
